import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, Eye, EyeOff, Gift } from 'lucide-react';
import { toast } from 'sonner';
import { US_STATES } from '../utils/states';
import { SmartCityAutocomplete } from '../components/SmartCityAutocomplete';
import LanguageSelector from '../components/LanguageSelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// PlanBlox Logo
const PlanBloxLogo = ({ className = "h-8 w-8" }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_b5e1c113-3c1b-4c22-aa5b-d15e9e791e88/artifacts/tf4zn6ok_App%20Icon%201%20.png"
    alt="PlanBlox"
    className={className}
  />
);

const Register = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    fullName: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    city: '',
    state: '',
    phoneNumber: '',
  });
  const [referralCode, setReferralCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [hpField, setHpField] = useState('');  // honeypot

  const { register } = useAuth();
  const navigate = useNavigate();

  // Check for referral code in URL or localStorage
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      const code = refCode.toUpperCase();
      setReferralCode(code);
      localStorage.setItem('planblox_ref', code);
    } else {
      // Check localStorage for previously stored referral code
      const stored = localStorage.getItem('planblox_ref');
      if (stored) setReferralCode(stored);
    }
  }, [searchParams]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleStateChange = (value) => {
    setFormData({ ...formData, state: value, city: '' });
  };

  const handleCityChange = (city, state) => {
    if (city && state) {
      setFormData({ ...formData, city, state });
    } else {
      setFormData({ ...formData, city: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!agreedToTerms) {
      toast.error(t('auth.mustAgreeTerms', 'You must agree to the Terms of Service and Privacy Policy'));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error(t('auth.passwordsNotMatch'));
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error(t('auth.passwordMinLength'));
      return;
    }

    if (!formData.state) {
      toast.error(t('auth.pleaseSelectState'));
      return;
    }

    if (!formData.city) {
      toast.error(t('auth.pleaseSelectCity'));
      return;
    }
    
    setIsLoading(true);
    try {
      await register(
        formData.email, 
        formData.password, 
        formData.fullName, 
        formData.username,
        formData.city,
        formData.state,
        referralCode || null,
        null,
        hpField || null
      );
      toast.success(t('auth.accountCreated') + ' You received 5 free credits!', { duration: 6000 });
      localStorage.removeItem('planblox_ref'); // Clear stored referral code
      navigate('/hub');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.registrationFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Language Selector - Top Right */}
      <div className="absolute top-4 right-4 z-10">
        <LanguageSelector variant="icon" />
      </div>
      
      {/* Left side - Image */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-primary/10 to-primary/5 items-center justify-center p-12">
        <div className="max-w-lg text-center space-y-6">
          <img 
            src="https://images.pexels.com/photos/35404740/pexels-photo-35404740.jpeg" 
            alt="Clean workspace"
            className="rounded-2xl shadow-2xl"
          />
          <h2 className="text-2xl font-bold text-foreground">{t('auth.joinPlanBlox')}</h2>
          <p className="text-muted-foreground">
            {t('auth.coordinateAcross')}
          </p>
        </div>
      </div>
      
      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 lg:p-12">
        <div className="w-full max-w-md space-y-8">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <PlanBloxLogo className="h-10 w-10 rounded-xl" />
            <span className="text-2xl font-bold tracking-tight">PlanBlox</span>
          </Link>
          
          <Card className="border-0 shadow-lg">
            <CardHeader className="space-y-1 pb-6">
              <CardTitle className="text-2xl">{t('auth.createAccount')}</CardTitle>
              <p className="text-sm text-emerald-600 dark:text-emerald-400 font-medium" data-testid="signup-free-access-note">
                {t('auth.freeStarterAccess', 'Sign up and get 5 free credits. Credits unlock advanced features.')}
              </p>
              <CardDescription>
                {t('auth.enterDetails')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Referral Code Banner */}
              {referralCode && (
                <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-2">
                  <Gift className="h-5 w-5 text-green-600 dark:text-green-400" />
                  <span className="text-sm text-green-700 dark:text-green-300">
                    {t('auth.codeApplied', { code: referralCode })}
                  </span>
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="fullName">{t('auth.fullName')}</Label>
                    <Input
                      id="fullName"
                      name="fullName"
                      placeholder="Jane Doe"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                      data-testid="register-fullname-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="username">{t('auth.username')}</Label>
                    <Input
                      id="username"
                      name="username"
                      placeholder="janedoe"
                      value={formData.username}
                      onChange={handleChange}
                      required
                      data-testid="register-username-input"
                    />
                  </div>
                </div>

                {/* Email + Verification */}
                <div className="space-y-2">
                  <Label htmlFor="email">{t('auth.email')}</Label>
                  <div className="flex gap-2">
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      data-testid="register-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">{t('auth.password')}</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      className="pr-10"
                      data-testid="register-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      data-testid="toggle-password-visibility"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">{t('auth.confirmPassword')}</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required
                      className="pr-10"
                      data-testid="register-confirm-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      data-testid="toggle-confirm-password-visibility"
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* Location Section */}
                <div className="pt-2 border-t">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-sm font-medium">{t('auth.location')}</span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="state">{t('auth.state')} *</Label>
                      <Select 
                        value={formData.state} 
                        onValueChange={handleStateChange}
                      >
                        <SelectTrigger data-testid="register-state-select">
                          <SelectValue placeholder={t('auth.selectState')} />
                        </SelectTrigger>
                        <SelectContent>
                          {US_STATES.map(state => (
                            <SelectItem key={state.value} value={state.value}>
                              {state.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="city">{t('auth.city')} *</Label>
                      <SmartCityAutocomplete
                        value={formData.city}
                        onChange={handleCityChange}
                        state={formData.state}
                        placeholder={formData.state ? t('auth.typeCityName') : t('auth.selectStateFirst')}
                        disabled={!formData.state}
                        dataTestId="register-city-input"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    {t('auth.changeInSettings')}
                  </p>
                </div>

                {/* Referral / Promo Code */}
                <div className="space-y-2">
                  <Label htmlFor="referralCode">{t('auth.referralPromoCode', 'Referral / Promo Code')}</Label>
                  <Input
                    id="referralCode"
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                    placeholder={t('auth.referralPromoPlaceholder', 'Optional')}
                    className="uppercase"
                    data-testid="register-referral-input"
                  />
                </div>

                {/* Terms & Privacy Checkbox */}
                <div className="flex items-start gap-2 pt-1" data-testid="terms-checkbox-section">
                  <input
                    type="checkbox"
                    id="agreeTerms"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                    data-testid="agree-terms-checkbox"
                  />
                  <label htmlFor="agreeTerms" className="text-sm text-muted-foreground cursor-pointer leading-snug">
                    {t('auth.agreeToTerms', 'I agree to the')}{' '}
                    <Link to="/terms" target="_blank" className="text-primary hover:underline font-medium" data-testid="terms-link">
                      {t('auth.termsOfService', 'Terms of Service')}
                    </Link>
                    {' '}{t('common.and', 'and')}{' '}
                    <Link to="/privacy" target="_blank" className="text-primary hover:underline font-medium" data-testid="privacy-link">
                      {t('auth.privacyPolicy', 'Privacy Policy')}
                    </Link>
                  </label>
                </div>

                {/* Honeypot - hidden from real users */}
                <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true" tabIndex={-1}>
                  <input
                    type="text"
                    name="website"
                    value={hpField}
                    onChange={(e) => setHpField(e.target.value)}
                    autoComplete="off"
                    tabIndex={-1}
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full rounded-full" 
                  disabled={isLoading || !agreedToTerms}
                  data-testid="register-submit-btn"
                >
                  {isLoading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t('auth.creatingAccount')}</>
                  ) : (
                    t('auth.createAccount')
                  )}
                </Button>
              </form>
              
              <div className="mt-6 text-center text-sm text-muted-foreground">
                {t('auth.alreadyHaveAccount')}{' '}
                <Link to="/login" className="text-primary hover:underline font-medium" data-testid="login-link">
                  {t('auth.signIn')}
                </Link>
              </div>
            </CardContent>
          </Card>
          
          <div className="text-center">
            <Link to="/" className="text-sm text-muted-foreground hover:text-primary transition-colors">
              ← {t('auth.backToWelcome')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
